import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { EvaluationList } from './evaluation/EvaluationList';
import { EvaluationDetails } from './evaluation/EvaluationDetails';
import { criteresService } from '@/services';
import { toast } from '@/hooks/use-toast';

interface Evaluation {
  id?: string;
  ao_id: string;
  nom: string;
  description: string;
  ordre: number;
  ponderation_globale_pct: number;
}
interface CritereEvaluation {
  id?: string;
  nom: string;
  description: string;
  ponderation_pct: number;
  contenu_html: string;
  ordre: number;
  evaluation_id?: string;
}
interface EvaluationsManagerProps {
  aoId: string;
  initialEvaluations?: Evaluation[];
  initialCriteresMap?: Record<number, CritereEvaluation[]>;
  onEvaluationsChange: (evaluations: Evaluation[]) => void;
  onCriteresChange?: (evaluationIndex: number, criteres: CritereEvaluation[]) => void;
}
export const EvaluationsManager: React.FC<EvaluationsManagerProps> = ({
  aoId,
  initialEvaluations = [],
  initialCriteresMap,
  onEvaluationsChange,
  onCriteresChange
}) => {
  const [evaluations, setEvaluations] = useState<Evaluation[]>(initialEvaluations);
  const [activeEvaluationId, setActiveEvaluationId] = useState<string | null>(null);
  const [criteresMap, setCriteresMap] = useState<Record<string, CritereEvaluation[]>>({});
  const [totalPonderation, setTotalPonderation] = useState(0);
  // Convert initialCriteresMap (index-based) to ID-based on mount
  useEffect(() => {
    // Skip if either collection is missing
    if (!initialEvaluations || initialEvaluations.length === 0) return;
    
    const idBasedMap: Record<string, CritereEvaluation[]> = {};
    
    // Initialize all evaluations with empty arrays or their criteres
    initialEvaluations.forEach((evaluation, index) => {
      const evalId = evaluation.id || `new-${index}`;
      const criteres = initialCriteresMap?.[index] || [];
      idBasedMap[evalId] = criteres;
    });
    
    setCriteresMap(idBasedMap);
  }, [initialEvaluations, initialCriteresMap]);
  useEffect(() => {
    setEvaluations(initialEvaluations);
    
    // Auto-select the first evaluation if none is selected
    if (initialEvaluations.length > 0 && !activeEvaluationId) {
      const firstEvalId = initialEvaluations[0].id || 'new-0';
      setActiveEvaluationId(firstEvalId);
    }
  }, [initialEvaluations]);
  useEffect(() => {
    const total = evaluations.reduce((sum, evaluation) => sum + evaluation.ponderation_globale_pct, 0);
    setTotalPonderation(total);
  }, [evaluations]);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      onEvaluationsChange(evaluations);
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [evaluations]);

  const loadCriteres = async (evaluationId: string, evalId: string) => {
    try {
      const data = await criteresService.getByEvaluationId(evaluationId);
      setCriteresMap(prev => ({
        ...prev,
        [evalId]: data || []
      }));
    } catch (error) {
      console.error('Erreur lors du chargement des critères:', error);
    }
  };
  const handleSelectEvaluation = (evalId: string) => {
    setActiveEvaluationId(evalId);
    
    // Load criteres if not already loaded
    const evaluation = evaluations.find((e, idx) => (e.id || `new-${idx}`) === evalId);
    if (evaluation?.id && !criteresMap[evalId]) {
      loadCriteres(evaluation.id, evalId);
    } else if (!criteresMap[evalId]) {
      setCriteresMap(prev => ({ ...prev, [evalId]: [] }));
    }
  };

  const addEvaluation = () => {
    const newEvaluation: Evaluation = {
      ao_id: aoId,
      nom: '',
      description: '',
      ordre: evaluations.length + 1,
      ponderation_globale_pct: 0
    };
    const newEvaluations = [...evaluations, newEvaluation];
    setEvaluations(newEvaluations);
    
    // Auto-select the new evaluation
    const newEvalId = `new-${evaluations.length}`;
    setActiveEvaluationId(newEvalId);
    setCriteresMap(prev => ({ ...prev, [newEvalId]: [] }));
  };
  const updateEvaluationField = (field: keyof Evaluation, value: string | number) => {
    if (!activeEvaluationId) return;
    
    const index = evaluations.findIndex((e, idx) => (e.id || `new-${idx}`) === activeEvaluationId);
    if (index === -1) return;
    
    const updatedEvaluations = [...evaluations];
    updatedEvaluations[index] = {
      ...updatedEvaluations[index],
      [field]: value
    };
    setEvaluations(updatedEvaluations);
  };

  const removeActiveEvaluation = () => {
    if (evaluations.length <= 1) {
      toast({
        title: "Erreur",
        description: "Au moins une évaluation est requise",
        variant: "destructive"
      });
      return;
    }
    
    if (!activeEvaluationId) return;
    
    const index = evaluations.findIndex((e, idx) => (e.id || `new-${idx}`) === activeEvaluationId);
    if (index === -1) return;
    
    const updatedEvaluations = evaluations.filter((_, i) => i !== index);
    setEvaluations(updatedEvaluations);
    setActiveEvaluationId(null);
    
    // Remove from criteresMap
    const newCriteresMap = { ...criteresMap };
    delete newCriteresMap[activeEvaluationId];
    setCriteresMap(newCriteresMap);
  };
  const distributeEqually = () => {
    if (evaluations.length === 0) return;
    const equalWeight = Math.floor(100 / evaluations.length);
    const remainder = 100 % evaluations.length;
    const updatedEvaluations = evaluations.map((evaluation, index) => ({
      ...evaluation,
      ponderation_globale_pct: equalWeight + (index < remainder ? 1 : 0)
    }));
    setEvaluations(updatedEvaluations);
  };
  const handleCriteresChange = useCallback((criteres: CritereEvaluation[]) => {
    if (!activeEvaluationId) return;
    
    setCriteresMap(prev => {
      // Don't update if content is the same
      const existingCriteres = prev[activeEvaluationId];
      if (existingCriteres && JSON.stringify(existingCriteres) === JSON.stringify(criteres)) {
        return prev;
      }
      
      return {
        ...prev,
        [activeEvaluationId]: criteres
      };
    });
    
    // Notify parent with index-based format for backward compatibility
    if (onCriteresChange) {
      const index = evaluations.findIndex((e, idx) => (e.id || `new-${idx}`) === activeEvaluationId);
      if (index !== -1) {
        onCriteresChange(index, criteres);
      }
    }
  }, [activeEvaluationId, evaluations, onCriteresChange]);
  const activeEvaluation = evaluations.find((e, idx) => (e.id || `new-${idx}`) === activeEvaluationId) || null;
  const activeCriteres = activeEvaluationId ? (criteresMap[activeEvaluationId] || []) : [];

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-4 h-full min-h-[600px]">
      <EvaluationList
        evaluations={evaluations}
        activeEvaluationId={activeEvaluationId}
        criteresMap={criteresMap}
        totalPonderation={totalPonderation}
        onSelectEvaluation={handleSelectEvaluation}
        onAddEvaluation={addEvaluation}
        onDistributeEqually={distributeEqually}
      />
      
      <EvaluationDetails
        evaluation={activeEvaluation}
        evaluationId={activeEvaluationId}
        aoId={aoId}
        criteres={activeCriteres}
        canDelete={evaluations.length > 1}
        onUpdateField={updateEvaluationField}
        onDelete={removeActiveEvaluation}
        onCriteresChange={handleCriteresChange}
      />
    </div>
  );
};